import React from 'react';
import { 
  FiUsers, 
  FiCreditCard, 
  FiDollarSign, 
  FiActivity,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import UnderservedPopulationGrowthChart from './UnderservedPopulationGrowthChart';
import AverageLoanGapTrendChart from './AverageLoanGapTrendChart';
import LoanGapByAmountRangeChart from './LoanGapByAmountRangeChart';
import BorrowerCapacityUtilizationChart from './BorrowerCapacityUtilizationChart';
import UnderservedDemographicsChart from './UnderservedDemographicsChart';

interface MetricCardProps {
  title: string;
  value: string | number;
  change: string;
  target: string;
  icon: React.ReactNode;
  isPositive: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  target, 
  icon, 
  isPositive 
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
      <div className="flex items-start justify-between">
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-600 font-medium text-xs sm:text-sm">{title}</h3>
            <div className="bg-slate-100 p-1.5 sm:p-2 rounded-full">
              {icon}
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">{value}</p>
            <div className="flex items-center space-x-2">
              <span className={`flex items-center text-xs sm:text-sm font-medium ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? (
                  <FiArrowUp className="mr-1" size={12} />
                ) : (
                  <FiArrowDown className="mr-1" size={12} />
                )}
                {change}
              </span>
            </div>
          </div>
          
          <div className="pt-3 border-t border-slate-200">
            <p className="text-slate-500 text-xs sm:text-sm">
              Target: <span className="font-medium text-slate-700">{target}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface GapAnalysisRow {
  loanRange: string;
  applications: number;
  approved: number;
  approvalRate: string;
  avgGap: string;
  gapColor: string;
}

const GapAnalysisTable: React.FC = () => {
  const data: GapAnalysisRow[] = [
    {
      loanRange: '$0-1,000',
      applications: 45,
      approved: 42,
      approvalRate: '93%',
      avgGap: '$67',
      gapColor: 'text-green-600'
    },
    {
      loanRange: '$1,000-5,000',
      applications: 120,
      approved: 105,
      approvalRate: '88%',
      avgGap: '$1,250',
      gapColor: 'text-green-600'
    },
    {
      loanRange: '$5,000-10,000',
      applications: 85,
      approved: 68,
      approvalRate: '80%',
      avgGap: '$2,720',
      gapColor: 'text-red-600'
    },
    {
      loanRange: '$10,000-25,000',
      applications: 65,
      approved: 48,
      approvalRate: '74%',
      avgGap: '$4,250',
      gapColor: 'text-red-600'
    },
    {
      loanRange: '$25,000+',
      applications: 25,
      approved: 15,
      approvalRate: '60%',
      avgGap: '$8,200',
      gapColor: 'text-red-600'
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-slate-200">
      <h4 className="text-lg font-medium text-slate-700 mb-6">Detailed Gap Analysis by Loan Range</h4>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-3 px-4 font-medium text-slate-700">Loan Range</th>
              <th className="text-left py-3 px-4 font-medium text-slate-700">Applications</th>
              <th className="text-left py-3 px-4 font-medium text-slate-700">Approved</th>
              <th className="text-left py-3 px-4 font-medium text-slate-700">Approval Rate</th>
              <th className="text-left py-3 px-4 font-medium text-slate-700">Avg Gap</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-medium text-slate-800">{row.loanRange}</td>
                <td className="py-4 px-4 text-slate-600">{row.applications}</td>
                <td className="py-4 px-4 text-slate-600">{row.approved}</td>
                <td className="py-4 px-4 text-slate-600">{row.approvalRate}</td>
                <td className={`py-4 px-4 font-semibold ${row.gapColor}`}>{row.avgGap}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const UnderservedMarket: React.FC = () => {
  const underservedMetrics = [
    {
      title: 'Credit Invisible Served',
      value: '62',
      change: '+24%',
      target: '80',
      icon: <FiUsers className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'First-time Borrowers',
      value: '105',
      change: '+19.3%',
      target: '130',
      icon: <FiCreditCard className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Avg Loan Gap',
      value: '$1,600',
      change: '-23.8%',
      target: '<$1,000',
      icon: <FiDollarSign className="text-slate-600" size={20} />,
      isPositive: false
    },
    {
      title: 'Capacity Utilization',
      value: '72%',
      change: '+8.3%',
      target: '75%',
      icon: <FiActivity className="text-slate-600" size={20} />,
      isPositive: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header Text */}
      <div>
        <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Measure impact on credit invisible and first-time borrowers</h3>
        
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {underservedMetrics.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>
      </div>

      {/* Population Growth & Gap Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Underserved Population Growth</h4>
          <div className="h-96 sm:h-80">
            <UnderservedPopulationGrowthChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Average Loan Gap Trend</h4>
          <div className="h-96 sm:h-80">
            <AverageLoanGapTrendChart />
          </div>
        </div>
      </div>

      {/* Loan Gap Range & Capacity Utilization Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan Gap by Amount Range</h4>
          <div className="h-96 sm:h-80">
            <LoanGapByAmountRangeChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Borrower Capacity Utilization</h4>
          <div className="h-96 sm:h-80">
            <BorrowerCapacityUtilizationChart />
          </div>
        </div>
      </div>

      {/* Demographics Performance Chart */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Underserved Demographics Performance</h4>
        <div className="h-96 sm:h-80">
          <UnderservedDemographicsChart />
        </div>
      </div>

      {/* Gap Analysis Table */}
      <GapAnalysisTable />
    </div>
  );
};

export default UnderservedMarket;